// SpaServiceMaster.js  Master CRUD for Spa Services.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Switch, FormControlLabel, Chip, Snackbar, Alert,
  CircularProgress, InputAdornment, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SpaIcon from '@mui/icons-material/Spa';
import { spaApi, ctx } from './modulesApi';

const EMPTY = {
  serviceId: 0, companyId: 0, hotelId: 0,
  serviceName: '', serviceCategory: '',
  durationMinutes: 60, price: 0, gstPercent: 18, hsnCode: '',
  isActive: true,
};

const DURATIONS = [30, 45, 60, 90, 120];

export default function SpaServiceMaster() {
  const { companyId, hotelId, userId, currency } = ctx();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const res = await spaApi.listServices(hotelId, {
        page: page + 1, pageSize: rowsPerPage, search,
      });
      setItems(res.data.items || []);
      setTotal(res.data.totalCount || 0);
    } catch (e) { notify('Failed to load services', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage]);

  const onSearch = () => { setPage(0); load(); };

  const onSave = async () => {
    if (!form.serviceName) return notify('Service name required', 'warning');
    if (form.price <= 0) return notify('Price must be > 0', 'warning');
    setSaving(true);
    try {
      await spaApi.saveService({ ...form, companyId, hotelId }, userId);
      notify(form.serviceId ? 'Service updated' : 'Service created');
      setOpen(false); setForm(EMPTY); load();
    } catch { notify('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await spaApi.deleteService(id, userId);
      notify('Service deleted'); load();
    } catch { notify('Delete failed', 'error'); }
  };

  const onEdit = (row) => {
    setForm({ ...row });
    setOpen(true);
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Card>
        <CardContent>
          <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
            <Box display='flex' alignItems='center' gap={1}>
              <SpaIcon color='primary' fontSize='large' />
              <Typography variant='h5'>Spa Service Master</Typography>
            </Box>
            <Button variant='contained' startIcon={<AddIcon />}
              onClick={() => { setForm({ ...EMPTY, companyId, hotelId }); setOpen(true); }}>
              Add Service
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size='small' label='Search service' value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={onSearch}><SearchIcon /></IconButton>
                    </InputAdornment>
                  ),
                }} />
            </Grid>
          </Grid>

          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>HSN Code</TableCell>
                  <TableCell align='right'>Duration</TableCell>
                  <TableCell align='right'>Price ({currency})</TableCell>
                  <TableCell align='right'>GST %</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={8} align='center'>
                    <CircularProgress size={24} />
                  </TableCell></TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow><TableCell colSpan={8} align='center'>
                    No services found
                  </TableCell></TableRow>
                )}
                {items.map((s) => (
                  <TableRow key={s.serviceId} hover>
                    <TableCell>{s.serviceName}</TableCell>
                    <TableCell>{s.serviceCategory || ''}</TableCell>
                    <TableCell>{s.hsnCode || '-'}</TableCell>
                    <TableCell align='right'>{s.durationMinutes} min</TableCell>
                    <TableCell align='right'>{Number(s.price).toFixed(2)}</TableCell>
                    <TableCell align='right'>{s.gstPercent}</TableCell>
                    <TableCell>
                      <Chip size='small' label={s.isActive ? 'Active' : 'Inactive'}
                        color={s.isActive ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align='right'>
                      <IconButton size='small' onClick={() => onEdit(s)}><EditIcon /></IconButton>
                      <IconButton size='small' color='error' onClick={() => onDelete(s.serviceId)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination component='div' count={total}
            page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} />
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{form.serviceId ? 'Edit Service' : 'Add Service'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label='Service Name' value={form.serviceName}
                onChange={(e) => setForm({ ...form, serviceName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label='Category' value={form.serviceCategory || ''}
                onChange={(e) => setForm({ ...form, serviceCategory: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth label='Duration (min)'
                value={form.durationMinutes}
                onChange={(e) => setForm({ ...form, durationMinutes: parseInt(e.target.value, 10) })}>
                {DURATIONS.map((d) => <MenuItem key={d} value={d}>{d} min</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type='number' label='Price' value={form.price}
                onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type='number' label='GST %' value={form.gstPercent}
                onChange={(e) => setForm({ ...form, gstPercent: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth label='HSN Code' value={form.hsnCode || ''}
                onChange={(e) => setForm({ ...form, hsnCode: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={
                <Switch checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              } label='Active' />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
}
